import {
  ListingsPromoBannerFragment,
  ListingsPromoBannerQuery,
  ListingsPromoBannerQueryVariables,
} from "@outschool/gql-frontend-generated";
import { useLocale } from "@outschool/localization";
import { gql, useQuery } from "@outschool/ui-apollo";

import { BannerType } from "./BannerType";

export const LISTINGS_PROMO_BANNER_FRAGMENT = gql`
  fragment ListingsPromoBannerButton on ContentfulListingsPromoBannerButton {
    buttonText
    buttonSearchFilters {
      adminTags
      ages
      capacityMax
      capacityMin
      deliveryTypes
      durationWeeksMax
      durationWeeksMin
      endBy
      formats
      fundingPrograms
      gradeLevel
      hasFilledOutUniqueLearningNeeds
      pricePerCourseMax
      pricePerCourseMin
      pricePerMeetingMax
      pricePerMeetingMin
      multiTermQuery
      query
      startAfter
      startBefore
      subject
      uniqueLearningNeeds
      weeklyMeetingsMin
      weeklyMeetingsMax
    }
    exploreMoreButtonText
  }

  fragment ListingsPromoBanner on ContentfulListingsPromoBanner {
    sys {
      publishedVersion
    }
    name
    experimentVersion
    headerTitle
    includeControlGroup
    headerTitleFontDefinition {
      fontFamily
    }
    headerSubtitle
    sidebarTitle
    backgroundImageLeft {
      url
    }
    backgroundImageRight {
      url
    }
    backgroundImageMobile {
      url
    }
    buttonsCollection {
      items {
        ...ListingsPromoBannerButton
      }
    }
  }
`;

const LISTINGS_PROMO_BANNER_QUERY = gql`
  query ListingsPromoBanner(
    $locale: String
    $preview: Boolean!
    $isLiveOnSearch: Boolean!
  ) {
    contentfulListingsPromoBannerCollection(
      limit: 1
      skip: 0
      where: { isLiveOnSearch: $isLiveOnSearch }
      preview: $preview
      locale: $locale
    ) {
      items {
        ...ListingsPromoBanner
      }
    }
  }
  ${LISTINGS_PROMO_BANNER_FRAGMENT}
`;

export function useFetchListingPromoBanners({
  bannerType,
  skip,
}: {
  bannerType: BannerType;
  skip: boolean;
}) {
  const locale = useLocale();

  const {
    data: bannerData,
    loading: bannersFetchLoading,
    error: bannersFetchError,
  } = useQuery<ListingsPromoBannerQuery, ListingsPromoBannerQueryVariables>(
    LISTINGS_PROMO_BANNER_QUERY,
    {
      variables: {
        isLiveOnSearch: bannerType === BannerType.Search,
        locale,
        preview: false,
      },
      skip,
    }
  );

  const banners =
    bannerData?.contentfulListingsPromoBannerCollection?.items.filter(
      (
        b: ListingsPromoBannerFragment | null
      ): b is ListingsPromoBannerFragment & { name: string } => !!b?.name
    ) ?? [];

  return {
    banners,
    bannersFetchLoading,
    bannersFetchError,
  };
}
